(() => {
  'use strict';
  if (customElements.get('color-collection')) return;

  class ColorCollection extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-color-grid]');
      this.preview = this.querySelector('[data-color-preview]');
      if (!this.grid || !this.preview) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.activeTile = null;
      this.loading = false;
      this.positionFrame = null;
      this.lastPointerType = '';
      this.diagonal = false;
      this.next = this.querySelector('[data-color-next]');
      this.status = this.querySelector('[data-color-status]');
      this.image = this.querySelector('[data-preview-image]');
      this.imageStatus = this.querySelector('[data-preview-image-status]');
      this.image.addEventListener('error', () => {
        this.image.hidden = true;
        this.imageStatus.hidden = false;
      }, options);
      this.image.addEventListener('load', () => {
        this.image.hidden = false;
        this.imageStatus.hidden = true;
      }, options);

      this.grid.addEventListener('pointerdown', event => { this.lastPointerType = event.pointerType; }, options);
      this.grid.addEventListener('pointerover', event => {
        if (!this.hoverMedia.matches || event.pointerType === 'touch') return;
        const tile = event.target.closest('[data-color-product]');
        if (tile) this.showPreview(tile, false, event);
      }, options);
      this.grid.addEventListener('pointermove', event => {
        if (this.activeTile && !this.preview.classList.contains('is-touch')) this.positionPreview(event);
      }, options);
      this.grid.addEventListener('pointerleave', () => {
        if (!this.grid.contains(document.activeElement) && !this.preview.classList.contains('is-touch')) this.hidePreview();
      }, options);
      this.grid.addEventListener('focusin', event => {
        const tile = event.target.closest('[data-color-product]');
        if (tile && !this.closing && this.hoverMedia.matches && this.lastPointerType !== 'touch') this.showPreview(tile, false);
      }, options);
      this.grid.addEventListener('focusout', event => {
        if (!this.grid.contains(event.relatedTarget) && !this.preview.classList.contains('is-touch')) this.hidePreview();
      }, options);
      this.grid.addEventListener('click', event => {
        const tile = event.target.closest('[data-color-product]');
        if (!tile || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const touch = event.detail !== 0 && (this.lastPointerType === 'touch' || !this.hoverMedia.matches);
        if (touch && typeof this.preview.showModal === 'function') {
          event.preventDefault();
          this.showPreview(tile, true);
        }
      }, options);
      this.querySelector('[data-color-close]').addEventListener('click', () => this.hidePreview(), options);
      this.preview.addEventListener('close', () => {
        if (!this.preview.open) this.clearActive();
      }, options);
      this.preview.addEventListener('click', event => {
        if (event.target !== this.preview) return;
        const rect = this.preview.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.hidePreview();
      }, options);
      document.addEventListener('keydown', event => {
        this.lastPointerType = '';
        if (event.key === 'Escape' && this.preview.open) this.hidePreview();
      }, options);
      window.addEventListener('resize', () => {
        this.layoutProducts();
        if (!this.preview.classList.contains('is-touch')) this.hidePreview();
      }, options);
      this.prepareColors(this.grid.querySelectorAll('[data-color-product]'));
      const colorToggle = this.querySelector('[data-color-toggle]');
      if (colorToggle) {
        this.grid.classList.remove('is-color-only');
        colorToggle.setAttribute('aria-checked', 'false');
        colorToggle.hidden = false;
        colorToggle.addEventListener('click', () => {
          const enabled = this.grid.classList.toggle('is-color-only');
          colorToggle.setAttribute('aria-checked', String(enabled));
          this.hidePreview();
        }, options);
      }
      const diagonalToggle = this.querySelector('[data-diagonal-toggle]');
      if (diagonalToggle) {
        diagonalToggle.setAttribute('aria-checked', 'false');
        diagonalToggle.hidden = false;
        diagonalToggle.addEventListener('click', () => {
          this.diagonal = !this.diagonal;
          diagonalToggle.setAttribute('aria-checked', String(this.diagonal));
          this.hidePreview();
          this.layoutProducts();
        }, options);
      }
      this.layoutProducts();
      if (this.next) {
        this.next.addEventListener('click', event => {
          event.preventDefault();
          this.loadRemaining();
        }, options);
      }
      this.loadRemaining();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.hidePreview();
    }

    prepareColors(tiles) {
      tiles.forEach(tile => {
        const color = (tile.dataset.averageColor || '').trim();
        if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) {
          tile.style.setProperty('--atlas-tile-color', color);
          tile.setAttribute('data-color-ready', '');
        } else if (color) {
          console.warn('[Colour atlas] Invalid custom.average_img_color; keeping product photo.', tile.href, color);
        }
      });
    }

    layoutProducts() {
      const tiles = Array.from(this.grid.querySelectorAll('[data-color-product]'));
      if (!this.diagonal) {
        tiles.forEach(tile => tile.style.removeProperty('order'));
        return;
      }
      const columns = Number(getComputedStyle(this).getPropertyValue('--atlas-columns'));
      const total = Math.max(Number(this.dataset.total) || 0, tiles.length);
      const rows = Math.ceil(total / columns);
      let index = 0;
      // Skip unused cells in the last row so a complete collection has no gaps.
      for (let diagonal = 0; diagonal < rows + columns - 1 && index < tiles.length; diagonal++) {
        const firstRow = Math.max(0, diagonal - columns + 1);
        const lastRow = Math.min(rows - 1, diagonal);
        for (let row = firstRow; row <= lastRow && index < tiles.length; row++) {
          const column = diagonal - row;
          const position = row * columns + column;
          if (position >= total) continue;
          tiles[index].style.setProperty('order', String(position));
          index++;
        }
      }
    }

    showPreview(tile, touch, pointer) {
      if (typeof this.preview.show !== 'function') return;
      if (this.activeTile !== tile || touch !== this.preview.classList.contains('is-touch')) {
        this.hidePreview();
        this.activeTile = tile;
        tile.classList.add('is-active');
        this.grid.classList.add('has-active');
        this.querySelector('[data-preview-title]').textContent = tile.dataset.title;
        this.querySelector('[data-preview-price]').textContent = tile.dataset.price;
        this.querySelector('[data-preview-availability]').textContent = tile.dataset.availability;
        this.querySelector('[data-preview-number]').textContent = String(tile.dataset.number).padStart(3, '0');
        this.querySelector('[data-preview-link]').href = tile.href;
        this.image.hidden = true;
        this.imageStatus.hidden = true;
        this.image.alt = tile.dataset.imageAlt || tile.dataset.title;
        if (tile.dataset.preview) {
          this.image.src = tile.dataset.preview;
          if (this.image.complete && this.image.naturalWidth) this.image.hidden = false;
        } else {
          this.image.removeAttribute('src');
          this.imageStatus.hidden = false;
        }
        this.preview.classList.toggle('is-touch', touch);
        if (touch) {
          this.preview.removeAttribute('aria-hidden');
          this.preview.style.left = '';
          this.preview.style.top = '';
          this.preview.showModal();
          document.documentElement.classList.add('color-atlas-modal-open');
        } else {
          this.preview.setAttribute('aria-hidden', 'true');
          this.preview.setAttribute('open', '');
        }
      }
      if (!touch) this.positionPreview(pointer, true);
    }

    positionPreview(pointer, immediate = false) {
      const tileRect = this.activeTile.getBoundingClientRect();
      const x = pointer ? pointer.clientX : tileRect.right;
      const y = pointer ? pointer.clientY : tileRect.top;
      if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
      const position = () => {
        this.positionFrame = null;
        const rect = this.preview.getBoundingClientRect();
        const gap = 22;
        const left = x + rect.width + gap < window.innerWidth ? x + gap : x - rect.width - gap;
        this.preview.style.left = `${Math.max(12, Math.min(left, window.innerWidth - rect.width - 12))}px`;
        this.preview.style.top = `${Math.max(12, Math.min(y - rect.height / 3, window.innerHeight - rect.height - 12))}px`;
      };
      if (immediate) position();
      else this.positionFrame = requestAnimationFrame(position);
    }

    clearActive() {
      if (this.activeTile) this.activeTile.classList.remove('is-active');
      this.activeTile = null;
      if (this.grid) this.grid.classList.remove('has-active');
      document.documentElement.classList.remove('color-atlas-modal-open');
    }

    hidePreview() {
      this.closing = true;
      if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
      this.positionFrame = null;
      if (this.preview && this.preview.open) {
        if (this.preview.classList.contains('is-touch')) this.preview.close();
        else this.preview.removeAttribute('open');
      }
      this.clearActive();
      this.closing = false;
    }

    async loadRemaining() {
      if (this.loading || !this.next || !this.next.getAttribute('href')) return;
      this.loading = true;
      this.next.hidden = true;
      this.status.classList.remove('is-error');
      this.status.textContent = this.dataset.loadingText;
      try {
        const visited = new Set();
        while (this.next.getAttribute('href')) {
          const url = new URL(this.next.href, window.location.origin);
          if (url.origin !== window.location.origin || visited.has(url.href)) throw new Error('Invalid colour collection pagination URL');
          visited.add(url.href);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error(`Colour collection request failed (${response.status})`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = Array.from(html.querySelectorAll('color-collection')).find(item => item.dataset.sectionId === this.dataset.sectionId);
          const tiles = section && section.querySelectorAll('[data-color-product]');
          if (!tiles || !tiles.length) throw new Error('Colour collection response contains no products');
          if (!this.isConnected) return;
          this.prepareColors(tiles);
          const fragment = document.createDocumentFragment();
          tiles.forEach(tile => fragment.appendChild(document.importNode(tile, true)));
          this.grid.appendChild(fragment);
          this.layoutProducts();
          this.querySelector('[data-loaded-count]').textContent = this.grid.childElementCount;
          const next = section.querySelector('[data-color-next]');
          if (next) this.next.setAttribute('href', next.getAttribute('href'));
          else this.next.removeAttribute('href');
        }
        this.status.textContent = this.dataset.completeText;
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[Colour atlas]', error);
        this.status.textContent = this.dataset.errorText;
        this.status.classList.add('is-error');
        this.next.hidden = false;
      } finally {
        this.loading = false;
      }
    }
  }

  customElements.define('color-collection', ColorCollection);
})();
